import { InteractionType } from "@azure/msal-browser";
import { MsalAuthenticationTemplate } from "@azure/msal-react";
import { QueryClientProvider } from "@tanstack/react-query";
import { CircleCheckIcon, InfoIcon, Loader2Icon, OctagonXIcon, TriangleAlertIcon } from "lucide-react";
import React, { Profiler, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes, useLocation } from "react-router";
import type { ToasterProps } from "sonner";
import { useAnalyticsSink } from "@/analytics/useAnalyticsSink";
import { usePageViewTracking } from "@/analytics/usePageViewTracking";
import { Toaster } from "@/components/ui/sonner";
import { AUTH_BYPASS } from "./auth/MsalConfig";
import { MsalIntegration } from "./auth/MsalIntegration";
import { AuthBypassBanner } from "./components/AuthBypassBanner";
import EmptyPage from "./components/EmptyPage";
import { AppLayout, NavButton } from "./components/layout";
import { VersionBanner } from "./components/VersionBanner";
import { queryClient } from "./queryClient";
import { getRouteConfig, routes } from "./routes";
import { LayoutProvider } from "./state/LayoutContext";
import { PermissionProvider } from "./state/PermissionContext";

import "./App.css";
import "./components/table/table.css";
import { features } from "./config";

const ReactQueryDevtools = import.meta.env.DEV
  ? React.lazy(() => import("@tanstack/react-query-devtools").then((m) => ({ default: m.ReactQueryDevtools })))
  : () => null;

const TestList = React.lazy(() => import("@/pages/testlist"));
const ProcessingTestList = React.lazy(() => import("@/pages/processingtestlist/ProcessingTestList"));
const TestDetails = React.lazy(() => import("@/pages/tests/TestDetails"));
const ProcessingTestDetails = React.lazy(() => import("@/pages/processingdetails/ProcessingTestDetails"));
const NewTestsPage = React.lazy(() => import("@/pages/importtests"));
const AccessManagement = React.lazy(() => import("@/pages/accessmanagement"));
const WcBulkInput = React.lazy(() => import("@/features/wc/bulk-input/WcBulkInputPage"));
const PsdHydrometerBulkInput = React.lazy(() => import("@/features/psd/bulk-input/PsdHydrometerBulkPage"));
const AgsProjectsPage = React.lazy(() => import("@/pages/ags"));
const AgsProjectPage = React.lazy(() => import("@/pages/ags/AgsProjectPage"));
const ReportsPage = React.lazy(() => import("@/features/reports/ReportsPage"));
const CreateReportPage = React.lazy(() => import("@/features/reports/CreateReportPage"));
const ReportContainerPage = React.lazy(() => import("@/features/reports/ReportContainerPage"));
const TestConditionsPage = React.lazy(() => import("@/features/test-conditions/TestConditionsPage"));

const NotFound = () => {
  const { t } = useTranslation("app");
  return <EmptyPage title={t("notFound.title", "404")} subTitle={t("notFound.subtitle", "Page not found")} />;
};
const Loading = () => {
  const { t } = useTranslation("app");
  return <EmptyPage title={t("loading.title", "Loading...")} subTitle="" />;
};

const onAppRender: React.ProfilerOnRenderCallback | undefined = import.meta.env.DEV
  ? (id, phase, actualDuration, baseDuration, startTime, commitTime) => {
      console.debug(`[Profiler:${id}] phase=${phase}`, {
        actualDuration,
        baseDuration,
        startTime,
        commitTime,
      });
    }
  : undefined;

const ProtectedContent = ({ children }: { children: React.ReactNode }) => {
  if (AUTH_BYPASS) {
    return <>{children}</>;
  }
  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={{ scopes: ["User.Read"] }}
      loadingComponent={Loading}
    >
      {children}
    </MsalAuthenticationTemplate>
  );
};

const Shell = () => {
  const { pathname } = useLocation();
  usePageViewTracking(pathname);
  const { t } = useTranslation("app");
  const noPadding = getRouteConfig(pathname)?.noPadding ?? false;
  return (
    <PermissionProvider>
      <AppLayout
        nav={
          <>
            <NavButton
              icon="biotech"
              label={t("nav.tests", "Tests")}
              href={routes.tests.root}
              selected={pathname.startsWith(routes.tests.root) || pathname.startsWith(routes.processingTests.root)}
            />
            <NavButton
              icon="layers"
              label={t("nav.ags", "AGS")}
              href={routes.ags.root}
              style={!features.previewFeatures ? { display: "none" } : undefined}
              selected={pathname.startsWith(routes.ags.root)}
            />
            <NavButton
              icon="description"
              label={t("nav.reports", "Reports")}
              href={routes.reports.root}
              selected={pathname.startsWith(routes.reports.root)}
            />
          </>
        }
        navBottom={
          <NavButton
            icon="people"
            label={t("nav.access", "Access")}
            href={routes.accessManagement}
            style={!features.permissions ? { display: "none" } : undefined}
            selected={pathname.startsWith(routes.accessManagement)}
          />
        }
      >
        <div className={noPadding ? "root-content root-content--flush" : "root-content"}>
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path={routes.tests.root} element={<TestList />}>
                <Route path={routes.tests.new} element={<NewTestsPage />} />
                {features.previewFeatures && <Route path={routes.tests.conditions} element={<TestConditionsPage />} />}
                <Route path={routes.tests.bulkInput} element={<WcBulkInput />} />
                <Route path={routes.tests.psdHydrometerBulkInput} element={<PsdHydrometerBulkInput />} />
                <Route path={routes.tests.detail} element={<TestDetails />} />
              </Route>
              <Route path={routes.processingTests.root} element={<ProcessingTestList />}>
                <Route path={routes.processingTests.detail} element={<ProcessingTestDetails />} />
              </Route>
              <Route path={routes.accessManagement} element={<AccessManagement />} />
              <Route path={routes.ags.root} element={<AgsProjectsPage />} />
              <Route path={routes.ags.project} element={<AgsProjectPage />} />
              <Route path={routes.reports.root} element={<ReportsPage />} />
              <Route path={routes.reports.project} element={<ReportsPage />} />
              <Route path={routes.reports.create} element={<CreateReportPage />} />
              <Route path={routes.reports.container} element={<ReportContainerPage />} />
              <Route index element={<Navigate to={routes.tests.root} replace />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </div>
      </AppLayout>
    </PermissionProvider>
  );
};

const toastIcons: ToasterProps["icons"] = {
  success: <CircleCheckIcon className="size-5 text-positive" />,
  info: <InfoIcon className="size-5 text-primary" />,
  warning: <TriangleAlertIcon className="size-5 text-warning" />,
  error: <OctagonXIcon className="size-5 text-destructive" />,
  loading: <Loader2Icon className="size-5 animate-spin text-muted-foreground" />,
};

function App() {
  useAnalyticsSink();
  return (
    <QueryClientProvider client={queryClient}>
      <Profiler id="AppRoot" onRender={onAppRender ?? (() => {})}>
        <LayoutProvider>
          {AUTH_BYPASS ? null : <MsalIntegration />}
          <AuthBypassBanner />
          <VersionBanner />
          <Toaster position="bottom-right" closeButton duration={5000} icons={toastIcons} />
          <Routes>
            <Route
              path="*"
              element={
                <ProtectedContent>
                  <Shell />
                </ProtectedContent>
              }
            />
          </Routes>
        </LayoutProvider>
      </Profiler>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
